import cn from 'classnames';
// React
import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
// Api
import { GET_MEDIA_POSTS } from 'api/mediaPost/queries';
// Types
import {
  GetMediaPost_getMediaPost,
  GetMediaPost_getMediaPost_ImagePost_store,
  GetMediaPost_getMediaPost_VideoPost_store,
} from 'api/mediaPost/types/GetMediaPost';
import { GetStream_stream_store } from 'api/streams/types/GetStream';
import {
  GetMediaPosts,
  GetMediaPostsVariables,
} from 'api/mediaPost/types/GetMediaPosts';
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import { StoreStatus } from 'api/graphql-global-types';
// Constants
import { MEDIA_POSTS_LIBRARY } from 'constants/routes';
// UI
import Slider from 'react-slick';
// Hooks
import { useBreakpoints, useCarouselArrows } from 'hooks';
import { useTrackViews, AnalyticsTargetType } from 'hooks/analytics';
// Components
import SectionHeader from 'components/common3/SectionHeader/SectionHeader';
import MediaFeedList from './MediaFeedList/MediaFeedList';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './RelatedMediaFeed.module.scss';

type RelatedMediaFeedProps = {
  store:
    | GetStoreBySlug_store
    | GetMediaPost_getMediaPost_ImagePost_store
    | GetMediaPost_getMediaPost_VideoPost_store
    | GetStream_stream_store;
  currentMediaFeed?: GetMediaPost_getMediaPost;
  title?: string;
  showPendingStoreItems?: boolean;
  className?: string;
  link?: string;
};

const RelatedMediaFeed = ({
  store,
  currentMediaFeed,
  title,
  showPendingStoreItems = false,
  className,
  link,
}: RelatedMediaFeedProps) => {
  const sliderRef = useRef<Slider | null>(null);
  const feedViewSentRef = useRef(false);
  const { isBreakpointL } = useBreakpoints();
  const { trackFeedViewBatch } = useTrackViews({
    targetType: AnalyticsTargetType.MEDIA_POST,
  });
  const { data } = useQuery<GetMediaPosts, GetMediaPostsVariables>(
    GET_MEDIA_POSTS,
    {
      variables: {
        input: {
          storeId: store.id,
          storeStatuses: [
            StoreStatus.Active,
            ...(showPendingStoreItems ? [StoreStatus.Pending] : []),
          ],
          limit: 8,
        },
      },
      // Was `network-only`. On the athlete home page this fired a second
      // GraphQL roundtrip after SSR had already populated the cache, leaving
      // the Media Feed section empty until the network resolved — visible
      // pop-in below the fold once the unified loading gate was removed.
      // `cache-and-network` paints from the SSR cache instantly and still
      // refreshes in the background.
      fetchPolicy: 'cache-and-network',
    }
  );

  const mediaFeeds = useMemo(
    () =>
      currentMediaFeed
        ? data?.getMediaPosts?.entities?.filter(
            ({ id }) => id !== currentMediaFeed.id
          ) || []
        : data?.getMediaPosts?.entities || [],
    [currentMediaFeed, data?.getMediaPosts?.entities]
  );

  // Batch track feed views for all fetched related posts (single request)
  useEffect(() => {
    if (mediaFeeds.length > 0 && !feedViewSentRef.current) {
      feedViewSentRef.current = true;
      trackFeedViewBatch(mediaFeeds.map((f) => f.id));
    }
  }, [mediaFeeds, trackFeedViewBatch]);

  const pageUrl = link ?? `${MEDIA_POSTS_LIBRARY}/${store.slug}`;

  const handlePrevClick = () => {
    sliderRef.current?.slickPrev();
  };

  const handleNextClick = () => {
    sliderRef.current?.slickNext();
  };

  const { canScroll, atStart, atEnd, onBeforeChange } = useCarouselArrows(
    sliderRef,
    mediaFeeds.length
  );

  if (!mediaFeeds.length) {
    return null;
  }

  // Mobile keeps the count-based threshold; desktop uses measurement.
  const showMobileArrows = mediaFeeds.length > 2;

  return (
    <section className={cn(styles.section, className)}>
      <SectionHeader
        title={title ?? 'Media Feed'}
        onPrev={isBreakpointL ? handlePrevClick : undefined}
        onNext={isBreakpointL ? handleNextClick : undefined}
        seeAllLink={pageUrl}
        seeAllLinkTitle="See Media"
        showArrows={isBreakpointL ? canScroll : false}
        prevDisabled={atStart}
        nextDisabled={atEnd}
      />
      <div className={styles.mediaFeedListContainer}>
        <MediaFeedList
          ref={sliderRef}
          mediaFeeds={mediaFeeds}
          withCarousel
          beforeChange={onBeforeChange}
        />
        {showMobileArrows && !isBreakpointL && (
          <div className={styles.mobileArrows}>
            <button
              className={styles.arrowButton}
              onClick={handlePrevClick}
              aria-label="Previous"
            >
              <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
            </button>
            <button
              className={styles.arrowButton}
              onClick={handleNextClick}
              aria-label="Next"
            >
              <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default RelatedMediaFeed;
